import React, { useRef, useEffect, useState } from "react";
import { Box, Slider, Grid2 } from "@mui/material";
import ThumbnailImage from "./ThumbnailImage";
import { IResultSet } from "./carouselHelpers";
import { useCarouselContext } from "../../context/CarouselContext";

const ThumbnailSlider: React.FC = () => {
  const { filteredImages, selectedImageIndex, handleGoTo } = useCarouselContext();
  const sliderContainerRef = useRef<HTMLDivElement | null>(null);
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);
  const [centerThumbnails, setCenterThumbnails] = useState(true);

  // 🖱️ Drag state refs
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const dragMoved = useRef(false);

  const hasMultipleImages = filteredImages.length > 1;

  useEffect(() => {
  const container = sliderContainerRef.current;
  const thumb = thumbnailRefs.current[selectedImageIndex];
  if (!container || !thumb) return;

  requestAnimationFrame(() => {
    const containerRect = container.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    const scrollOffset = thumb.offsetLeft - container.offsetWidth / 2 + thumb.offsetWidth / 2;

    container.scrollTo({
      left: scrollOffset,
      behavior: 'smooth',
    });
  });
}, [selectedImageIndex]);

  useEffect(() => {
    const container = sliderContainerRef.current;
    if (container) {
      const shouldCenter = container.scrollWidth <= container.clientWidth;
      setCenterThumbnails(shouldCenter);
    }
  }, [filteredImages]);

  // 💡 Add drag-to-scroll behavior
  useEffect(() => {
    const container = sliderContainerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      isDragging.current = true;
      dragMoved.current = false;
      startX.current = e.pageX;
      scrollLeft.current = container.scrollLeft;
      container.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const deltaX = Math.abs(e.pageX - startX.current);
      if (deltaX > 5) dragMoved.current = true;

      e.preventDefault();
      const walk = (e.pageX - startX.current) * 1;
      container.scrollLeft = scrollLeft.current - walk;
    };

    const stopDragging = () => {
      isDragging.current = false;
      if (container) container.style.cursor = 'grab';
    };

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", stopDragging);
    container.addEventListener("mouseleave", stopDragging);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseup", stopDragging);
      container.removeEventListener("mouseleave", stopDragging);
    };
  }, []);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const index = Math.floor(Array.isArray(value) ? value[0] : value);
    handleGoTo(index);
  };

  const handleGoToIfNotDragging = (idx: number) => {
    if (!dragMoved.current) {
      handleGoTo(idx);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Slider above thumbnails */}
      {hasMultipleImages && (
        <Box sx={{ display: 'flex', width: "100%", justifyContent: 'center' }}>
          <Slider
            value={selectedImageIndex}
            onChange={handleSliderChange}
            min={0}
            max={filteredImages.length - 1}
            sx={{
              height: 3,
              width: '80%',
              "& .MuiSlider-track": { backgroundColor: "red" },
              "& .MuiSlider-thumb": {
                backgroundColor: "red",
                width: 12,
                height: 12,
              },
              "& .MuiSlider-rail": { backgroundColor: "red", opacity: 0.2 },
            }}
          />
        </Box>
      )}

      {/* Thumbnails with drag scroll */}
      <Grid2
        ref={sliderContainerRef}
        size={{ xs: 12 }}
        sx={{
          height: '60px',
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          alignItems: "center",
          mt: hasMultipleImages ? 0 : 2,
          justifyContent: centerThumbnails ? "center" : "flex-start",
          whiteSpace: "nowrap",
          gap: 1,
          scrollbarWidth: "none",
          cursor: "grab",
          userSelect: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {filteredImages.map((image: IResultSet, idx: number) => (
          <ThumbnailImage
            key={idx}
            ref={(el) => (thumbnailRefs.current[idx] = el)}
            image={image.thumbnail || ''}
            tag={image.tag}
            index={idx}
            selectedImageIndex={selectedImageIndex}
            handleGoTo={handleGoToIfNotDragging}
          />
        ))}
      </Grid2>
    </Box>
  );
};

export default ThumbnailSlider;
